import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { ArrowLeft } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { SiteHeader } from '@/components/SiteHeader';
import { SiteFooter } from '@/components/SiteFooter';
import { ConcentricRings } from '@/components/Motifs';
import api from '@/lib/api';
import { usePageTitle } from '@/hooks/usePageTitle';

export default function FrameworkDetailPage() {
  const { slug } = useParams();
  const [framework, setFramework] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  usePageTitle(framework?.name);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get(`/frameworks/${slug}`);
        setFramework(res.data);
        const allRes = await api.get('/frameworks');
        setRelated(allRes.data.filter(f => f.slug !== slug).slice(0, 3));
      } catch (err) {
        setError('Unable to load framework.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen">
        <SiteHeader />
        <div className="flex justify-center py-32">
          <ConcentricRings size={60} opacity={0.3} />
        </div>
      </div>
    );
  }

  if (!framework) {
    return (
      <div className="min-h-screen">
        <SiteHeader />
        <div className="max-w-3xl mx-auto px-4 py-32 text-center">
          <h1 className="font-['Cormorant_Garamond'] text-3xl font-bold text-[hsl(var(--foreground))]">Framework not found</h1>
        </div>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <SiteHeader />

      <article className="py-12 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-12 lg:gap-16">
            {/* Main content */}
            <div>
              <Link
                to="/work"
                className="inline-flex items-center gap-2 font-['Inter'] text-sm text-[hsl(var(--silver))] hover:text-[hsl(var(--foreground))] transition-colors mb-8"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to The Work
              </Link>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              >
                <p className="font-['Inter'] text-xs font-medium uppercase tracking-[0.14em] text-[hsl(var(--gold))] mb-4">
                  {framework.tagline}
                </p>
                <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl lg:text-5xl font-bold text-[hsl(var(--foreground))] mb-6 leading-[1.1]">
                  {framework.name}
                </h1>
                <p className="font-['Inter'] text-base sm:text-lg leading-[1.7] text-[hsl(var(--muted-foreground))] mb-8">
                  {framework.summary}
                </p>

                <Separator className="bg-[rgba(201,168,76,0.15)] mb-10" />

                <div className="reading-room font-['Inter'] text-[15px] sm:text-base leading-[1.8] text-[hsl(var(--muted-foreground))]">
                  <ReactMarkdown>{framework.body}</ReactMarkdown>
                </div>
              </motion.div>
            </div>

            {/* Sidebar */}
            <aside className="hidden lg:block">
              <div className="sticky top-28 space-y-8">
                {/* Meta */}
                <div className="rounded-xl bg-[hsl(var(--card))] border border-[rgba(201,168,76,0.15)] p-6">
                  <h4 className="font-['Inter'] text-xs font-medium uppercase tracking-[0.14em] text-[hsl(var(--silver))] mb-4">
                    Details
                  </h4>
                  {framework.reading_time && (
                    <p className="font-['Inter'] text-sm text-[hsl(var(--muted-foreground))] mb-3">
                      {framework.reading_time} min read
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {framework.tags?.map(tag => (
                      <Badge
                        key={tag}
                        variant="outline"
                        className="font-['Inter'] text-[10px] font-medium uppercase tracking-[0.12em] border-[rgba(168,181,178,0.2)] text-[hsl(var(--silver))] bg-transparent"
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Related */}
                {related.length > 0 && (
                  <div className="rounded-xl bg-[hsl(var(--card))] border border-[rgba(201,168,76,0.15)] p-6">
                    <h4 className="font-['Inter'] text-xs font-medium uppercase tracking-[0.14em] text-[hsl(var(--silver))] mb-4">
                      Related Frameworks
                    </h4>
                    <div className="space-y-3">
                      {related.map(r => (
                        <Link
                          key={r.slug}
                          to={`/work/${r.slug}`}
                          className="block font-['Cormorant_Garamond'] text-base font-semibold text-[hsl(var(--foreground))] hover:text-[hsl(var(--gold))] transition-colors"
                        >
                          {r.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </aside>
          </div>
        </div>
      </article>

      <SiteFooter />
    </div>
  );
}
